import { Button, Menu } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue } from 'jotai';
import { LucideGlobe } from 'lucide-react';

import * as complianceRulesState from '../../_state/complianceRules.state';
import { useTranslations } from '../../i18n/useTranslations';

import { JURISDICTION_PACK_RULES } from './jurisdictionPackRules';

export const JurisdictionPackMenu = () => {
  const t = useTranslations();
  const toast = useToast();
  const facilityId = useAtomValue(complianceRulesState.$complianceFacilityId);
  const ruleDefinitions = useAtomValue(
    complianceRulesState.$rawRuleDefinitions,
  );
  const currentRules = useAtomValue(complianceRulesState.$currentFacilityRules);
  const [{ isPending }, createRule] = useAtom(
    complianceRulesState.$createFacilityRule,
  );

  const handleApplyPack = async (jurisdictionKey: string) => {
    if (!facilityId) return;

    const packRules = JURISDICTION_PACK_RULES[jurisdictionKey];
    if (!packRules) return;

    const defs = ruleDefinitions.data ?? [];
    const ruleCodeToDefId = new Map(defs.map((d) => [d.ruleCode, d.id]));

    const existingDefIds = new Set(
      (currentRules.data ?? []).map((r) => r.ruleDefinitionId),
    );
    const matchedRules = packRules.filter((rule) => {
      const defId = ruleCodeToDefId.get(rule.ruleCode);
      return defId && !existingDefIds.has(defId);
    });

    if (matchedRules.length === 0) {
      toast.error(
        t.complianceView.rules.jurisdictionPacks.messages.noMatchingRules,
      );
      return;
    }

    const results = await Promise.allSettled(
      matchedRules.map((rule) => {
        const ruleDefinitionId = ruleCodeToDefId.get(rule.ruleCode) ?? '';
        return createRule({
          facilityId,
          ruleDefinitionId,
          isEnabled: true,
          configuredValue: rule.value,
        });
      }),
    );
    const failures = results.filter((r) => r.status === 'rejected');
    const label =
      t.complianceView.rules.jurisdictionPacks.jurisdictions[
        jurisdictionKey as keyof typeof t.complianceView.rules.jurisdictionPacks.jurisdictions
      ] ?? jurisdictionKey;

    if (failures.length === 0) {
      toast.success(
        t.complianceView.rules.jurisdictionPacks.messages.applySuccess(label),
      );
    } else {
      const firstError = (failures[0] as PromiseRejectedResult).reason;
      toast.error(
        t.complianceView.rules.jurisdictionPacks.messages.applyError(
          firstError instanceof Error ? firstError.message : 'Unknown error',
        ),
      );
    }
  };

  const jurisdictions = Object.keys(JURISDICTION_PACK_RULES);

  return (
    <div className="@JurisdictionPackMenu contents">
      <Menu
        trigger={
          <Button
            size="small"
            color="neutral"
            variant="outline"
            icon={<LucideGlobe />}
            isDisabled={isPending}
          >
            {t.complianceView.rules.facilityRules.actions.applyJurisdictionPack}
          </Button>
        }
        items={jurisdictions.map((key) => ({
          label:
            t.complianceView.rules.jurisdictionPacks.jurisdictions[
              key as keyof typeof t.complianceView.rules.jurisdictionPacks.jurisdictions
            ] ?? key,
          onAction: () => handleApplyPack(key),
        }))}
      />
    </div>
  );
};
